import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil } from 'lucide-react';
import { isMultifocalLensType, type OpticalJobDetail } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { FormField } from '@/components/ui/FormField';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { apiErrorCode, apiErrorDetails } from '@/services/apiClient';
import { useOpticalLabsListQuery, useUpdateOpticalJobMutation } from '../../api/opticalJobs.api';
import { fittingProblem } from '../../builder/draft';
import { trackingErrorMessage } from '../../tracking/errors';

interface JobLabCardProps {
  job: OpticalJobDetail;
  /** The job is ORDERED and the user may prepare it. */
  canEdit: boolean;
  onSaved: (message: string) => void;
  onDirtyChange: (dirty: boolean) => void;
  /** Someone else changed the job: drop this edit and read the job again, never resend it. */
  onConflict: () => void;
}

interface LabForm {
  labId: string;
  labNotes: string;
  right: string;
  left: string;
}

const formOf = (job: OpticalJobDetail): LabForm => ({
  labId: job.lab?.id ?? '',
  labNotes: job.labNotes ?? '',
  right: job.rightFittingHeightMm ?? '',
  left: job.leftFittingHeightMm ?? '',
});

/**
 * The lab a job goes to, the lab's own reference, the notes for the lab and the fitting heights.
 *
 * While the job is ORDERED someone who may prepare it can choose among the active labs, write the notes and set the heights; each save names the version on screen, and a job someone else changed meanwhile is read again rather than overwritten. From the lab onward all of it is fixed. An inactive lab a past job went to is still named, marked as inactive.
 */
export function JobLabCard({ job, canEdit, onSaved, onDirtyChange, onConflict }: JobLabCardProps) {
  const { t } = useTranslation();
  const notesId = useId();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<LabForm>(() => formOf(job));
  const [showErrors, setShowErrors] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const labs = useOpticalLabsListQuery(false);
  const update = useUpdateOpticalJobMutation(job.id);
  // A second click lands before the button re-renders as busy: only the first may send.
  const inFlight = useRef(false);
  const firstField = useRef<HTMLSelectElement>(null);
  const opener = useRef<HTMLButtonElement>(null);
  const moveFocus = useRef(false);
  // Opening the form takes the place of the button that opened it, and closing it brings the button back: focus goes with them, so a keyboard user is never dropped at the top of the page.
  useEffect(() => {
    if (!moveFocus.current) return;
    moveFocus.current = false;
    (editing ? firstField.current : opener.current)?.focus();
  }, [editing]);
  const close = () => {
    moveFocus.current = true;
    setEditing(false);
    onDirtyChange(false);
  };

  const initial = formOf(job);
  const dirty = editing && JSON.stringify(form) !== JSON.stringify(initial);
  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  const needs = {
    right: job.lenses.right ? isMultifocalLensType(job.lenses.right.lensType) : false,
    left: job.lenses.left ? isMultifocalLensType(job.lenses.left.lensType) : false,
  };
  const problems = {
    right: fittingProblem(form.right, needs.right),
    left: fittingProblem(form.left, needs.left),
  };
  const notesTooLong = form.labNotes.length > 500;

  const open = () => {
    setForm(formOf(job));
    setShowErrors(false);
    setError(null);
    moveFocus.current = true;
    setEditing(true);
  };

  const save = () => {
    if (inFlight.current) return;
    setError(null);
    if (problems.right || problems.left || notesTooLong) {
      setShowErrors(true);
      return;
    }
    inFlight.current = true;
    update.mutate(
      {
        version: job.version,
        labId: form.labId || null,
        labNotes: form.labNotes.trim() || null,
        rightFittingHeightMm: form.right.trim() || null,
        leftFittingHeightMm: form.left.trim() || null,
      },
      {
        onSettled: () => {
          inFlight.current = false;
        },
        onSuccess: () => {
          close();
          onSaved(t('opticalOrders.job.labSaved'));
        },
        onError: (caught) => {
          if (apiErrorCode(caught) === 'OPTICAL_JOB_VERSION_CONFLICT') {
            close();
            onConflict();
            return;
          }
          // A lab deactivated since the list was read: say so, and offer the labs active now.
          if (apiErrorDetails(caught).some((detail) => detail.field === 'labId')) {
            setError(t('opticalOrders.job.labNotActive'));
            void labs.refetch();
            return;
          }
          setError(trackingErrorMessage(t, caught));
        },
      },
    );
  };

  const fittingError = (side: 'right' | 'left') => {
    const problem = problems[side];
    return showErrors && problem ? t(problem.key, problem.values) : undefined;
  };

  return (
    <Card>
      <CardHeader
        title={t('opticalOrders.job.labTitle')}
        subtitle={
          canEdit ? t('opticalOrders.job.labHintEditable') : t('opticalOrders.job.labHintFixed')
        }
        action={
          canEdit && !editing ? (
            <Button ref={opener} variant="secondary" size="sm" onClick={open}>
              <Pencil className="size-4" aria-hidden />
              {t('opticalOrders.job.prepare')}
            </Button>
          ) : undefined
        }
        wrapAction
      />
      {editing ? (
        <div className="space-y-4 px-5 py-4">
          {error ? <Alert tone="error">{error}</Alert> : null}
          <FormField label={t('opticalOrders.job.lab')}>
            {({ id, describedBy }) => (
              <Select
                ref={firstField}
                id={id}
                aria-describedby={describedBy}
                value={form.labId}
                onChange={(event) => setForm({ ...form, labId: event.target.value })}
                options={[
                  { value: '', label: t('opticalOrders.lab.later') },
                  ...(labs.data ?? []).map((lab) => ({
                    value: lab.id,
                    label: `${lab.name} · ${t(lab.isInHouse ? 'opticalOrders.labs.inHouse' : 'opticalOrders.labs.external')}`,
                  })),
                ]}
              />
            )}
          </FormField>
          <div className="grid gap-3 sm:grid-cols-2">
            <Input
              label={t('opticalOrders.job.fittingFor', { eye: 'OD' })}
              inputMode="decimal"
              dir="ltr"
              className="font-mono rtl:text-right"
              value={form.right}
              onChange={(event) => setForm({ ...form, right: event.target.value })}
              hint={
                needs.right
                  ? t('opticalOrders.job.fittingNeeded')
                  : t('opticalOrders.job.fittingOptional')
              }
              error={fittingError('right')}
            />
            <Input
              label={t('opticalOrders.job.fittingFor', { eye: 'OS' })}
              inputMode="decimal"
              dir="ltr"
              className="font-mono rtl:text-right"
              value={form.left}
              onChange={(event) => setForm({ ...form, left: event.target.value })}
              hint={
                needs.left
                  ? t('opticalOrders.job.fittingNeeded')
                  : t('opticalOrders.job.fittingOptional')
              }
              error={fittingError('left')}
            />
          </div>
          <div className="space-y-1.5">
            <label htmlFor={notesId} className="text-ink text-sm font-medium">
              {t('opticalOrders.job.labNotes')}
            </label>
            <textarea
              id={notesId}
              rows={3}
              maxLength={500}
              value={form.labNotes}
              onChange={(event) => setForm({ ...form, labNotes: event.target.value })}
              aria-describedby={`${notesId}-hint`}
              className="border-line focus:border-brand-500 focus:ring-brand-200 w-full rounded-lg border px-3 py-2 text-sm focus:ring-2 focus:outline-none"
            />
            <p id={`${notesId}-hint`} className="text-muted text-xs">
              {t('opticalOrders.job.labNotesHint', { count: form.labNotes.length })}
            </p>
          </div>
          <div className="border-line flex flex-wrap justify-end gap-2 border-t pt-4">
            <Button variant="secondary" onClick={close} disabled={update.isPending}>
              {t('common.cancel')}
            </Button>
            <Button onClick={save} isLoading={update.isPending} disabled={!dirty}>
              {t('opticalOrders.job.saveLab')}
            </Button>
          </div>
        </div>
      ) : (
        <dl className="grid gap-3 px-5 py-4 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-muted text-xs">{t('opticalOrders.job.lab')}</dt>
            <dd className="text-ink flex flex-wrap items-center gap-2">
              {job.lab ? (
                <>
                  <span dir="auto">{job.lab.name}</span>
                  <span className="text-muted text-xs">
                    {t(
                      job.lab.isInHouse
                        ? 'opticalOrders.labs.inHouse'
                        : 'opticalOrders.labs.external',
                    )}
                  </span>
                  {!job.lab.isActive ? (
                    <Badge tone="warning">{t('opticalOrders.labs.inactive')}</Badge>
                  ) : null}
                </>
              ) : (
                <span className="text-muted">{t('opticalOrders.tracking.noLab')}</span>
              )}
            </dd>
          </div>
          <div>
            <dt className="text-muted text-xs">{t('opticalOrders.job.labReference')}</dt>
            <dd className="text-ink">
              {job.labReference ? (
                <span className="font-mono" dir="ltr">
                  {job.labReference}
                </span>
              ) : (
                <span className="text-muted">{t('opticalOrders.job.notRecorded')}</span>
              )}
            </dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="text-muted text-xs">{t('opticalOrders.job.labNotes')}</dt>
            <dd className="text-ink whitespace-pre-wrap break-words" dir="auto">
              {job.labNotes ?? (
                <span className="text-muted">{t('opticalOrders.job.notRecorded')}</span>
              )}
            </dd>
          </div>
        </dl>
      )}
    </Card>
  );
}
